import { Box, Button, CloseButton, Group, Modal, Text, ThemeIcon } from '@mantine/core';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import BackgroundSvg from './modal-background-pattern.svg?react';

type FileManagementModalProps = {
  opened: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  onConfirm: () => void | Promise<void>;
  onCancel?: () => void;
  confirmDisabled?: boolean;
  confirmLoading?: boolean;
};

export function FileManagementModal({
  opened,
  onClose,
  title,
  subtitle,
  icon,
  children,
  onConfirm,
  onCancel,
  confirmDisabled = false,
  confirmLoading = false,
}: FileManagementModalProps) {
  const handleConfirm = async () => {
    try {
      await onConfirm();
    } catch (error) {
      notifyAndLogError(
        'Action failed',
        'An error occurred while performing the action. Please try again.',
        error,
        { title, action: 'modal_confirm' },
      );
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      onClose();
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} size="md" withCloseButton={false}>
      <Box style={{ position: 'absolute', top: 0, left: 0, right: 0 }}>
        <BackgroundSvg />
      </Box>

      <Box
        style={{
          position: 'absolute',
          top: 0,
          left: -5,
          right: -5,
          height: 16,
          backgroundImage: 'url("/images/modal-border-bg.png")',
          backgroundSize: '100% 100%',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
        }}
      />
      {icon && (
        <ThemeIcon size={48} radius="xl" bg="gray.2" color="gray.7" mt={16}>
          {icon}
        </ThemeIcon>
      )}
      <CloseButton
        c="var(--mantine-color-gray-5)"
        onClick={onClose}
        style={{
          position: 'absolute',
          top: '30px',
          right: '24px',
          zIndex: 1000,
        }}
      />

      <Box pt="xxl">
        <Text size="textLg" fw={FONT_WEIGHTS.semibold} c="gray.10">
          {title}
        </Text>

        {subtitle && (
          <Text size="textSm" fw={FONT_WEIGHTS.regular} c="gray.7" mt="4px">
            {subtitle}
          </Text>
        )}

        <Box mt="20px">{children}</Box>

        <Group gap="xs" mt="32px" grow>
          <Button variant="secondary" size="lg" onClick={handleCancel} flex={1}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="lg"
            onClick={handleConfirm}
            disabled={confirmDisabled}
            loading={confirmLoading}
            flex={1}
          >
            Confirm
          </Button>
        </Group>
      </Box>
    </Modal>
  );
}
